/**
 * Account-scoped custom COSHH substance — the wire shape returned by
 * `GET /api/mobile/v1/account/custom-coshh` and mutated by POST/PATCH on the
 * sibling routes. Mirrors Prisma `AccountCustomCoshh` (account-level catalogue
 * entries the user has added themselves, in addition to the global catalogue).
 *
 * Web parity:
 *   - GET/POST  → app/api/account-custom-coshh/route.ts
 *   - PATCH/DEL → app/api/account-custom-coshh/[coshhId]/route.ts
 *
 * These schemas are the SINGLE source of truth for both surfaces: the web
 * routes import them too (they previously carried their own hand-maintained
 * copies, which is how the mobile PATCH silently drifted to a 3-field subset
 * while the web one grew full SDS depth — #2274). Change a field here and both
 * routes move together; the row → wire mapping lives in
 * `lib/coshh/custom-coshh-fields.ts`.
 *
 * Routes are gated behind `FEATURE.COSHH` (lib/gating.ts) — the mobile route
 * returns `PLAN_LOCKED` (HTTP 403) when the caller's plan doesn't include it.
 */
import { z } from "zod";

/**
 * Date fields cross the wire as strings (`yyyy-mm-dd` from a date picker, or a
 * full ISO timestamp) and are converted to `Date` at the route boundary, so an
 * invalid value must be refused HERE — otherwise it either reaches Prisma as an
 * `Invalid Date` (500) or, worse, is silently accepted as some other date.
 *
 * `Date.parse` is the wrong test for that: it is deliberately lenient, so
 * `2026-02-30` normalises to 2 March and the UK-style `09/02/2026` parses as
 * 2 September. Both would persist a date the user never entered. These two
 * primitives are calendar-correct instead — `.date()` rejects 30 February and
 * a 29 February in a non-leap year, and `.datetime({ offset: true })` applies
 * the same calendar check to the date half while requiring `Z` or a `±HH:MM`
 * offset, so a timestamp can never be stored against an ambiguous zone.
 */
const CalendarDate = z.string().date();
const IsoTimestamp = z.string().datetime({ offset: true });

const IsoDateString = z
  .string()
  .refine(
    (v) => CalendarDate.safeParse(v).success || IsoTimestamp.safeParse(v).success,
    {
      message:
        "Expected a calendar date (YYYY-MM-DD) or an ISO-8601 timestamp with a UTC offset",
    },
  );

export const CustomCoshhSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    category: z.string(),
    welLimit: z.string().nullable(),
    healthEffects: z.string().nullable(),
    // Assessment depth — editable on both surfaces, rendered on the substance
    // detail view. Every column is nullable in Prisma.
    controlMeasures: z.string().nullable(),
    exposureRoutes: z.string().nullable(),
    ppeRequired: z.string().nullable(),
    firstAidMeasures: z.string().nullable(),
    // G5-1 SDS depth — populated by SDS extraction or a manual edit.
    manufacturer: z.string().nullable(),
    sdsVersion: z.string().nullable(),
    /** ISO timestamp (Prisma `DateTime?`), or null. */
    sdsIssueDate: z.string().nullable(),
    hazardStatements: z.string().nullable(),
    precautionaryStatements: z.string().nullable(),
    fireMeasures: z.string().nullable(),
    storage: z.string().nullable(),
    disposal: z.string().nullable(),
    /**
     * ISO timestamp for the substance's next SDS review, or null. Drives the
     * "SDS review overdue / due in Nd" badge on the web library — the mobile
     * screen needs the raw date to derive the same status.
     */
    sdsReviewDueAt: z.string().nullable(),
    /**
     * Private storage reference to the source SDS PDF, set when the substance was
     * created from an uploaded Safety Data Sheet. Re-opened via the presigned
     * `/account/custom-coshh/[coshhId]/sds` route — never rendered/opened raw.
     * Null for manually-added substances.
     */
    sdsFileUrl: z.string().nullable(),
  });
export type CustomCoshh = z.infer<typeof CustomCoshhSchema>;

/**
 * #2411 server-boundary variant. The READ shape above is what the mobile app
 * parses off the wire, so it is permissive — an installed build must tolerate
 * a field the server adds. The Create/Update REQUEST shapes below stay
 * `.strict()`: rejecting an unexpected key there costs no installed client
 * anything, and the server validates input it controls.
 */
export const CustomCoshhStrict = CustomCoshhSchema.strict();

/**
 * Maximum lengths of the custom COSHH text fields. One source for the create
 * and update schemas below AND the SDS extractor (`lib/coshh/extract-sds.ts`),
 * which caps every extracted field to these, so a reviewed Safety Data Sheet
 * extraction can always be saved. The database columns are unbounded text;
 * these only bound the request. Sized for real data sheets: in UAT on 14 Oct
 * 2026 a 205-character exposure-limit line (several substances, each with its
 * limits) failed the old 200 cap and the whole save was refused.
 */
export const CUSTOM_COSHH_TEXT_LIMITS = {
  name: 200,
  category: 100,
  welLimit: 1000,
  healthEffects: 4000,
  controlMeasures: 4000,
  exposureRoutes: 4000,
  ppeRequired: 4000,
  firstAidMeasures: 4000,
  manufacturer: 500,
  sdsVersion: 100,
  hazardStatements: 8000,
  precautionaryStatements: 8000,
  fireMeasures: 4000,
  storage: 4000,
  disposal: 4000,
} as const;
const L = CUSTOM_COSHH_TEXT_LIMITS;

/**
 * Body for POST. The server upserts on `(accountId, name)`; re-adding the same
 * name overwrites the existing row rather than failing.
 *
 * The base three fields (name/welLimit/healthEffects) power the manual "+ Add"
 * form. The remaining fields carry full G5-1 SDS depth + the relink id when the
 * substance is created from an uploaded Safety Data Sheet, mirroring the web
 * `POST /api/account-custom-coshh` body. All optional so the manual path is
 * unaffected.
 */
export const CustomCoshhCreateSchema = z
  .object({
    name: z.string().min(1).max(L.name),
    welLimit: z.string().max(L.welLimit).optional(),
    healthEffects: z.string().max(L.healthEffects).optional(),
    // SDS depth (optional; populated from a Safety Data Sheet extraction).
    category: z.string().max(L.category).optional(),
    controlMeasures: z.string().max(L.controlMeasures).optional(),
    exposureRoutes: z.string().max(L.exposureRoutes).optional(),
    ppeRequired: z.string().max(L.ppeRequired).optional(),
    firstAidMeasures: z.string().max(L.firstAidMeasures).optional(),
    manufacturer: z.string().max(L.manufacturer).optional(),
    sdsVersion: z.string().max(L.sdsVersion).optional(),
    sdsIssueDate: IsoDateString.optional(),
    hazardStatements: z.string().max(L.hazardStatements).optional(),
    precautionaryStatements: z.string().max(L.precautionaryStatements).optional(),
    fireMeasures: z.string().max(L.fireMeasures).optional(),
    storage: z.string().max(L.storage).optional(),
    disposal: z.string().max(L.disposal).optional(),
    sdsReviewDueAt: IsoDateString.optional(),
    /**
     * Id of the UploadedSDS row produced by the SDS extract route. When present
     * AND that upload has a stored file, the server links the stored PDF onto
     * the substance (sdsFileUrl/uploadedSdsId) + back-links approvedSubstanceId.
     */
    uploadedSdsId: z.string().optional(),
  })
  .strict();
export type CustomCoshhCreate = z.infer<typeof CustomCoshhCreateSchema>;

/**
 * Body for PATCH — the full create field set, partial. Every field is optional
 * so PATCH stays a partial update, and everything except `name`/`category`
 * accepts `null` so the client can explicitly clear it.
 *
 * `category` is deliberately NOT nullable: the column is non-null with a
 * "Custom" default, so a null would 500 at the database. Omit it to leave the
 * current value alone (the web form does exactly that on a blank input).
 *
 * `uploadedSdsId` is create-only — an edit must never be able to re-point (or
 * clear) the stored SDS link. Web parity: the web PATCH schema omits it too.
 */
export const CustomCoshhUpdateSchema = z
  .object({
    name: z.string().min(1).max(L.name).optional(),
    category: z.string().max(L.category).optional(),
    welLimit: z.string().max(L.welLimit).nullable().optional(),
    healthEffects: z.string().max(L.healthEffects).nullable().optional(),
    controlMeasures: z.string().max(L.controlMeasures).nullable().optional(),
    exposureRoutes: z.string().max(L.exposureRoutes).nullable().optional(),
    ppeRequired: z.string().max(L.ppeRequired).nullable().optional(),
    firstAidMeasures: z.string().max(L.firstAidMeasures).nullable().optional(),
    manufacturer: z.string().max(L.manufacturer).nullable().optional(),
    sdsVersion: z.string().max(L.sdsVersion).nullable().optional(),
    sdsIssueDate: IsoDateString.nullable().optional(),
    hazardStatements: z.string().max(L.hazardStatements).nullable().optional(),
    precautionaryStatements: z.string().max(L.precautionaryStatements).nullable().optional(),
    fireMeasures: z.string().max(L.fireMeasures).nullable().optional(),
    storage: z.string().max(L.storage).nullable().optional(),
    disposal: z.string().max(L.disposal).nullable().optional(),
    sdsReviewDueAt: IsoDateString.nullable().optional(),
  })
  .strict();
export type CustomCoshhUpdate = z.infer<typeof CustomCoshhUpdateSchema>;
